import { PlusOutlined } from "@ant-design/icons";
import { Position } from "@xyflow/react";
import { ccSelectedNodeAtom } from "components/CC/atoms/diagramAtom.ts";
import { BaseNodeComponentInput } from "components/CC/Nodes/node-types.ts";
import { NodeContextMenu } from "components/CC/Nodes/NodeBase/NodeContextMenu.tsx";
import { SortOrderBadge } from "components/CC/Nodes/NodeIcons";
import { ConnectionHandle } from "components/CC/UI/ConnectionHandle.tsx";
import { CustomHandle } from "components/CC/UI/CustomHandle.tsx";
import { TransparentHandle } from "components/CC/UI/TransparentHandle.tsx";
import { PropsWithChildren, ReactNode } from "react";
import { useRecoilValue } from "recoil";

interface BaseNodeComponentProps extends BaseNodeComponentInput {
    color: string;
    label?: ReactNode;
    badge?: ReactNode;
    size?: number;
}

export const BaseNode = ({
    children,
    id,
    data,
    color,
    label,
    badge,
    size = 80,
}: PropsWithChildren<BaseNodeComponentProps>) => {
    const {
        onClick,
        onLeftHandleClick,
        onRightHandleClick,
        sortOrder,
        showPlusLeft,
        showPlusRight,
        onAddNode,
        formData,
        onContextMenuSettings,
        onContextMenuRename,
        onContextMenuDelete,
        disableContextMenu,
    } = data;

    const selectedNode = useRecoilValue(ccSelectedNodeAtom);
    const isSelected = selectedNode?.id === id;

    // Get custom title/subtitle/active from form data
    const customTitle = (formData as Record<string, unknown> | undefined)?.title as string | undefined;
    const subtitle = (formData as Record<string, unknown> | undefined)?.subtitle as string | undefined;
    const isActive = (formData as Record<string, unknown> | undefined)?.active !== false; // Default to true if not set

    // Use custom title if provided, otherwise use default label
    const displayLabel = customTitle || label;

    // Container size needs to accommodate the circle + handles
    const containerSize = size + 5;
    // Icon should fill ~60% of the circle
    const iconSize = Math.round(size * 0.6);

    // Half-circle plus button dimensions
    const plusButtonWidth = 20;
    const plusButtonHeight = 40;

    return (
        <NodeContextMenu
            onSettings={onContextMenuSettings}
            onRename={onContextMenuRename}
            onDelete={onContextMenuDelete}
            disabled={disableContextMenu}
        >
            <div
                className="flex flex-col items-center transition-opacity"
                style={{
                    overflow: "visible",
                    opacity: !isActive ? 0.5 : selectedNode && !isSelected ? 0.35 : 1,
                }}
            >
                <div
                    className="relative flex items-center justify-center"
                    style={{ width: containerSize, height: containerSize, overflow: "visible" }}
                >
                    {onLeftHandleClick ? (
                        <div className="absolute top-1/2 left-3 -translate-y-1/2 z-10">
                            <CustomHandle
                                id={`${id}-left`}
                                type="target"
                                color={color}
                                position={Position.Left}
                                onClick={() => onLeftHandleClick(id)}
                            />
                        </div>
                    ) : (
                        <TransparentHandle id={`${id}-left`} position={Position.Left} type="target" />
                    )}

                    {/* Right handle - positioned consistently whether visible (ConnectionHandle) or invisible (TransparentHandle) */}
                    {onRightHandleClick ? (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 z-10">
                            <CustomHandle
                                id={`${id}-right`}
                                type="source"
                                color={color}
                                position={Position.Right}
                                onClick={() => onRightHandleClick(id)}
                            />
                        </div>
                    ) : !showPlusRight ? (
                        /* When there's a connection, position TransparentHandle at same spot as ConnectionHandle */
                        <div
                            className="absolute"
                            style={{
                                right: -20,
                                top: "50%",
                                transform: "translateY(-50%)",
                            }}
                        >
                            <TransparentHandle id={`${id}-right`} position={Position.Right} type="source" />
                        </div>
                    ) : null}

                    {/* Half-circle plus button on left - behind node circle */}
                    {showPlusLeft && onAddNode && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddNode("left");
                            }}
                            className="absolute flex items-center justify-center cursor-pointer border-none"
                            style={{
                                width: plusButtonWidth,
                                height: plusButtonHeight,
                                left: "50%",
                                top: "50%",
                                transform: `translateX(-${size / 2 + plusButtonWidth - 4}px) translateY(-50%)`,
                                borderRadius: `${plusButtonWidth}px 0 0 ${plusButtonWidth}px`,
                                backgroundColor: "#e8f4fc",
                                zIndex: 5,
                            }}
                        >
                            <PlusOutlined style={{ color: "#7ec8e8", fontSize: 12, marginLeft: -2 }} />
                        </button>
                    )}

                    {/* Half-circle plus button on right - behind node circle */}
                    {showPlusRight && onAddNode && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddNode("right");
                            }}
                            className="absolute flex items-center justify-center cursor-pointer border-none"
                            style={{
                                width: plusButtonWidth,
                                height: plusButtonHeight,
                                left: "50%",
                                top: "50%",
                                transform: `translateX(${size / 2 - 4}px) translateY(-50%)`,
                                borderRadius: `0 ${plusButtonWidth}px ${plusButtonWidth}px 0`,
                                backgroundColor: "#e8f4fc",
                                zIndex: 5,
                            }}
                        >
                            <PlusOutlined style={{ color: "#7ec8e8", fontSize: 12, marginRight: -2 }} />
                        </button>
                    )}

                    {/* Connection handle next to plus button - for connecting to existing nodes */}
                    {/* Only render if showPlusRight AND not onRightHandleClick (to avoid duplicate handle IDs) */}
                    {showPlusRight && !onRightHandleClick && (
                        <div
                            className="absolute"
                            style={{
                                right: -20,
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 15,
                            }}
                        >
                            <ConnectionHandle id={`${id}-right`} type="source" position={Position.Right} />
                        </div>
                    )}

                    <div
                        onClick={onClick}
                        className={`relative rounded-[100%] z-10 flex items-center justify-center ${onClick ? "cursor-pointer" : ""}`}
                        style={{ backgroundColor: color, width: size, height: size }}
                    >
                        <div className="flex items-center justify-center text-white" style={{ fontSize: iconSize }}>
                            {children}
                        </div>
                        {badge}
                        {sortOrder !== undefined && <SortOrderBadge order={sortOrder} />}
                    </div>
                </div>

                {displayLabel && (
                    <div className="text-center" style={{ marginTop: 5 }}>
                        <div className="text-base font-semibold">{displayLabel}</div>
                        {subtitle && <div className="text-xs text-gray-500 max-w-[150px]">{subtitle}</div>}
                    </div>
                )}
            </div>
        </NodeContextMenu>
    );
};
